import React, { useState, useRef, useEffect } from "react";
import clsx from "clsx";
import { makeStyles } from "@material-ui/core/styles";
import { ChevronRight, ArrowLeftRight, GripVertical, ArrowUp, ArrowDown, X } from "lucide-react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import Dialog from "@material-ui/core/Dialog";
import Button from "../../../Shared/Buttons/Button";
import { Select as CustomSelect, SelectTrigger, SelectContent, SelectItem } from "../../../Shared/Select";
import { useTranslation } from "react-i18next";

/**
 * ProjectOrderBar
 *
 * Props:
 *   projects               {string[]}            – Ordered list of registration point names.
 *   onChange               {(string[]) => void}  – Called with the new order after Apply.
 *   disabled               {boolean}             – Disables all interaction.
 *   selectedProjectObjects {Array<{id, name}>}   – Optional. Full objects for selected projects
 *                                                   (enables add/remove mode in the dialog).
 *   allProjects            {Array<{id, projectName, resourceDescription}>}
 *                                                – Available (not-yet-selected) projects to add.
 *   onAdd                  {(projectId) => void} – Called immediately when a project is added.
 *   onRemove               {(projectId) => void} – Called immediately when a project is removed.
 */
function ProjectOrderBar({
  projects = [],
  onChange,
  disabled = false,
  selectedProjectObjects = null,
  allProjects = [],
  onAdd = null,
  onRemove = null,
}) {
  const { t } = useTranslation();
  const classes = useStyles();
  const scrollRef = useRef(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [localOrder, setLocalOrder] = useState([]);
  const [addSelection, setAddSelection] = useState([]);

  const selectionMode = onAdd !== null || onRemove !== null;

  // No longer needed — search is handled inside CustomSelect

  const buildLocalOrder = () =>
    selectedProjectObjects
      ? selectedProjectObjects.map((p) => ({ id: p.id, name: p.name }))
      : projects.map((name, i) => ({ id: i, name }));

  // Only sync from props when dialog is closed, to avoid clobbering in-dialog changes
  useEffect(() => {
    if (!dialogOpen) setLocalOrder(buildLocalOrder());
  }, [projects, selectedProjectObjects, dialogOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Scroll horizontally with the mouse wheel
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onWheel = (e) => {
      if (e.deltaY !== 0) {
        el.scrollLeft += e.deltaY;
        e.preventDefault();
      }
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  const handleDragEnd = (result) => {
    if (!result.destination) return;
    const items = Array.from(localOrder);
    const [moved] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, moved);
    setLocalOrder(items);
  };

  const handleOpen = () => {
    setLocalOrder(buildLocalOrder());
    setAddSelection([]);
    setDialogOpen(true);
  };

  const handleCancel = () => {
    setLocalOrder(buildLocalOrder());
    setAddSelection([]);
    setDialogOpen(false);
  };

  const handleApply = () => {
    if (onChange) onChange(localOrder.map((item) => item.name));
    setDialogOpen(false);
  };

  const handleAddProject = (project) => {
    setLocalOrder((prev) => [...prev, { id: project.id, name: project.resourceDescription }]);
    if (onAdd) onAdd(project.id);
  };

  const handleRemoveProject = (projectId) => {
    setLocalOrder((prev) => prev.filter((item) => item.id !== projectId));
    if (onRemove) onRemove(projectId);
  };

  return (
    <>
      <div className={clsx(classes.container, { [classes.containerDisabled]: disabled })}>
        {/* Scrollable project list */}
        <div ref={scrollRef} className={classes.scrollArea}>
          {projects.map((project, index) => (
            <React.Fragment key={`${project}-${index}`}>
              <span className={classes.projectName}>{project}</span>
              {index < projects.length - 1 && (
                <ChevronRight className={classes.chevron} />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Gradient fade overlay — sits on top of the scroll area just before the action column */}
        <div className={classes.fadeOverlay} />

        {/* Fixed action column: separator + reorder button */}
        <div className={classes.actionArea}>
          <div className={classes.separator} />
          <button
            className={classes.reorderButton}
            onClick={handleOpen}
            disabled={disabled}
            aria-label={t('projectOrderBar.reorderAriaLabel')}
          >
            <ArrowLeftRight size={16} strokeWidth={1.75} />
          </button>
        </div>
      </div>

      {/* Manage / Reorder dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCancel}
        classes={{ paper: classes.dialog }}
      >
        <div className={classes.dialog__inner}>
          <h2 className={classes.dialog__title}>
            {selectionMode ? t('projectOrderBar.manageTitle') : t('projectOrderBar.reorderTitle')}
          </h2>
          <p className={classes.dialog__subtitle}>
            {selectionMode
              ? t('projectOrderBar.manageSubtitle')
              : t('projectOrderBar.reorderSubtitle')}
          </p>

          {/* Add section – only visible in selection mode */}
          {selectionMode && (
            <div className={classes.dialog__addSection}>
              <CustomSelect
                value={addSelection}
                onChange={(newValues) => {
                  const added = newValues.filter((v) => !addSelection.includes(v));
                  added.forEach((projectId) => {
                    const project = allProjects.find((p) => p.id === projectId);
                    if (project) handleAddProject(project);
                  });
                  setAddSelection([]);
                }}
                multiple
                searchable
                placeholder={t('projectOrderBar.searchPlaceholder')}
                fullwidth
              >
                <SelectTrigger />
                <SelectContent>
                  {allProjects.length === 0 ? (
                    <SelectItem value="" searchText="">{t('projectOrderBar.noProjectsAvailable')}</SelectItem>
                  ) : (
                    allProjects.map((project) => (
                      <SelectItem
                        key={project.id}
                        value={project.id}
                        searchText={`${project.projectName} ${project.resourceDescription}`}
                      >
                        {project.projectName} – {project.resourceDescription}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </CustomSelect>
            </div>
          )}

          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="projectOrder">
              {(provided) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className={classes.dialog__list}
                >
                  {localOrder.map((item, index) => {
                    const delta = selectionMode ? 0 : item.id - index;
                    return (
                      <Draggable
                        key={`item-${item.id}`}
                        draggableId={`item-${item.id}`}
                        index={index}
                      >
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            className={clsx(classes.dialog__item, {
                              [classes.dialog__itemDragging]: snapshot.isDragging,
                            })}
                          >
                            <div
                              {...provided.dragHandleProps}
                              className={classes.dialog__dragHandle}
                            >
                              <GripVertical size={16} strokeWidth={1.5} />
                            </div>
                            <span className={classes.dialog__itemName}>{item.name}</span>
                            {selectionMode && onRemove ? (
                              <button
                                className={classes.dialog__removeButton}
                                onClick={() => handleRemoveProject(item.id)}
                                aria-label={t('common.remove')}
                              >
                                <X size={14} strokeWidth={1.75} />
                              </button>
                            ) : (
                              delta !== 0 && (
                                <span
                                  className={clsx(classes.dialog__delta, {
                                    [classes.dialog__deltaUp]: delta > 0,
                                    [classes.dialog__deltaDown]: delta < 0,
                                  })}
                                >
                                  {delta > 0
                                    ? <ArrowUp size={11} strokeWidth={2.5} />
                                    : <ArrowDown size={11} strokeWidth={2.5} />}
                                  {Math.abs(delta)}
                                </span>
                              )
                            )}
                          </div>
                        )}
                      </Draggable>
                    );
                  })}
                  {provided.placeholder}
                  {localOrder.length === 0 && (
                    <p className={classes.dialog__emptyMessage}>{t('projectOrderBar.emptyMessage')}</p>
                  )}
                </div>
              )}
            </Droppable>
          </DragDropContext>

          <div className={classes.dialog__footer}>
            <Button variant="secondary" size="medium" onClick={handleCancel}>{t('common.cancel')}</Button>
            <Button variant="primary" size="medium" onClick={handleApply}>{t('common.apply')}</Button>
          </div>
        </div>
      </Dialog>
    </>
  );
}

export default ProjectOrderBar;

// Action area total width: 4px padding + 1px separator + 8px gap + 40px button + 4px padding = 57px
const ACTION_AREA_WIDTH = 57;

const useStyles = makeStyles(() => ({
  container: {
    position: "relative",
    height: "48px",
    display: "flex",
    alignItems: "center",
    backgroundColor: "#FFFFFF",
    border: "1px solid #EDEDED",
    borderRadius: "12px",
    overflow: "hidden",
    boxSizing: "border-box",
    width: "100%",
  },
  containerDisabled: {
    opacity: 0.5,
    pointerEvents: "none",
  },
  scrollArea: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    gap: "16px",
    height: "100%",
    overflowX: "auto",
    overflowY: "hidden",
    padding: "0 16px",
    // Reserve space so content doesn't start hidden under the action area
    paddingRight: `${ACTION_AREA_WIDTH + 16}px`,
    whiteSpace: "nowrap",
    scrollbarWidth: "none",
    msOverflowStyle: "none",
    "&::-webkit-scrollbar": {
      display: "none",
    },
  },
  projectName: {
    fontFamily: "Lato, sans-serif",
    fontSize: "16px",
    fontWeight: "normal",
    color: "#0C0E29",
    whiteSpace: "nowrap",
    flexShrink: 0,
  },
  chevron: {
    width: "16px",
    height: "16px",
    flexShrink: 0,
    color: "#0C0E29",
    strokeWidth: "1.4px",
  },
  // Gradient that fades the scroll content into the action area
  fadeOverlay: {
    position: "absolute",
    right: ACTION_AREA_WIDTH,
    top: 0,
    bottom: 0,
    width: "60px",
    background: "linear-gradient(to right, rgba(255,255,255,0), #FFFFFF)",
    pointerEvents: "none",
    zIndex: 1,
  },
  // The fixed right-side area (separator + button)
  actionArea: {
    position: "absolute",
    right: 0,
    top: 0,
    bottom: 0,
    width: ACTION_AREA_WIDTH,
    display: "flex",
    alignItems: "center",
    backgroundColor: "#FFFFFF",
    padding: "4px",
    gap: "8px",
    zIndex: 2,
    boxSizing: "border-box",
  },
  separator: {
    width: "1px",
    height: "40px",
    backgroundColor: "#EDEDED",
    flexShrink: 0,
  },
  reorderButton: {
    width: "40px",
    height: "40px",
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#F8F8F8",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    color: "#0C0E29",
    transition: "background-color 0.15s ease",
    lineHeight: 0,
    "&:hover:not(:disabled)": {
      backgroundColor: "#EDEDED",
    },
    "&:disabled": {
      cursor: "not-allowed",
    },
  },

  // ── Dialog ──────────────────────────────────────────────────────────────
  dialog: {
    borderRadius: "12px",
    minWidth: "360px",
    maxWidth: "480px",
    overflow: "hidden",
  },
  dialog__inner: {
    padding: "24px",
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  dialog__title: {
    fontFamily: "Lato, sans-serif",
    fontSize: "24px",
    fontWeight: "normal",
    color: "#0C0E29",
    margin: 0,
  },
  dialog__subtitle: {
    fontFamily: "Lato, sans-serif",
    fontSize: "14px",
    color: "#6B7280",
    margin: "0 0 8px 0",
  },
  dialog__list: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
    marginBottom: "16px",
    maxHeight: "360px",
    overflowY: "auto",
    paddingRight: "4px",
    // Thin scrollbar
    scrollbarWidth: "thin",
    scrollbarColor: "#EDEDED transparent",
    "&::-webkit-scrollbar": {
      width: "4px",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "#EDEDED",
      borderRadius: "2px",
    },
  },
  dialog__item: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    height: "44px",
    padding: "0 12px",
    backgroundColor: "#F8F8F8",
    border: "1px solid #EDEDED",
    borderRadius: "8px",
    fontFamily: "Lato, sans-serif",
    fontSize: "14px",
    color: "#0C0E29",
    userSelect: "none",
    boxSizing: "border-box",
  },
  dialog__itemDragging: {
    backgroundColor: "#EDEDED",
    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
  },
  dialog__dragHandle: {
    display: "flex",
    alignItems: "center",
    color: "#9CA3AF",
    cursor: "grab",
    flexShrink: 0,
    "&:active": {
      cursor: "grabbing",
    },
  },
  dialog__itemName: {
    flex: 1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  dialog__delta: {
    display: "inline-flex",
    alignItems: "center",
    gap: "2px",
    flexShrink: 0,
    fontFamily: "Lato, sans-serif",
    fontSize: "11px",
    fontWeight: "700",
    lineHeight: 1,
    padding: "2px 6px",
    borderRadius: "10px",
    letterSpacing: "0.02em",
  },
  dialog__deltaUp: {
    color: "#16a34a",
    backgroundColor: "#dcfce7",
  },
  dialog__deltaDown: {
    color: "#dc2626",
    backgroundColor: "#fee2e2",
  },
  dialog__footer: {
    display: "flex",
    justifyContent: "space-between",
    gap: "8px",
  },

  // ── Selection mode additions ─────────────────────────────────────────────
  dialog__addSection: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    marginBottom: "8px",
  },
  dialog__removeButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "24px",
    height: "24px",
    flexShrink: 0,
    backgroundColor: "transparent",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    color: "#9CA3AF",
    "&:hover": { backgroundColor: "#fee2e2", color: "#dc2626" },
  },
  dialog__emptyMessage: {
    fontFamily: "Lato, sans-serif",
    fontSize: "14px",
    color: "#9CA3AF",
    textAlign: "center",
    padding: "16px 0",
    margin: 0,
  },
}));
